import React, { ReactChild } from 'react';
import classnames from 'classnames';
import { Link } from 'react-router-dom';
import { css } from '@emotion/css';
import { EntitlementType } from '../../../../../../@types/entitlement';
import { useGetHasEntitlement } from '../../../../../../customHooks/useGetHasEntitlement.hook';
import { IDepositStatusActivated } from '../../../../../../@types/deposit';
import { safeFormatTimestamp } from '../../../../../../utils/dateFormat.util';
import { useIntl } from '@/i18n';

const screenSizeS = '576px';
const styles = {
  container: css`
    border: 1px solid rgb(214, 214, 214);
    border-radius: 4px;
    padding: 24px 16px 0 16px;

    @media screen and (min-width: ${screenSizeS}) {
      width: 530px;
    }
  `,
  statusContainer: css`
    display: flex;
    align-items: center;
    margin-bottom: 24px;
  `,
  positiveIcon: css`
    color: #00d9b1;
    font-size: 20px;
  `,
  statusTitle: css`
    margin-left: 12px;
    font-size: 14px;
  `,
  sectionContainer: css`
    padding-bottom: 24px;

    @media screen and (min-width: ${screenSizeS}) {
      display: flex;
      justify-content: space-between;
    }
  `,
  sectionTitle: css`
    font-size: 14px;
    font-weight: bold;
    padding-bottom: 8px;

    @media screen and (min-width: ${screenSizeS}) {
      width: 50%;
      padding-bottom: initial;
    }
  `,
  sectionText: css`
    font-size: 14px;

    @media screen and (min-width: ${screenSizeS}) {
      width: 50%;
    }
  `,
};

const Section: React.FC<{ titleId: string; content: ReactChild }> = ({ titleId, content }) => {
  const { t } = useIntl();

  return (
    <div className={styles.sectionContainer}>
      <p className={styles.sectionTitle}>{t({ id: titleId, defaultMessage: titleId })}</p>
      <div className={styles.sectionText}>{content}</div>
    </div>
  );
};

interface Props {
  state: IDepositStatusActivated;
}

const DepositStatusFrame: React.FC<Props> = ({ state }) => {
  const { t } = useIntl();
  const isEntitled = useGetHasEntitlement(EntitlementType.DEPOSIT_SERVICE);

  return (
    <div className={styles.container}>
      <span className={styles.statusContainer}>
        <i className={classnames('is24-icon-accept', styles.positiveIcon)} />
        <span className={styles.statusTitle}>
          {t({
            id: 'benefits.deposit.modal.overview.depositStatusFrame.statusTitle',
            defaultMessage: 'Bürgschaft bei der Allianz aktiv',
          })}
        </span>
      </span>

      <Section
        titleId='benefits.deposit.modal.overview.depositStatusFrame.sectionTitles.customerNumber'
        content={<p>{state.applicationNumber}</p>}
      />
      <Section
        titleId='benefits.deposit.modal.overview.depositStatusFrame.sectionTitles.depositStart'
        content={<p>{safeFormatTimestamp(state.lastStatusUpdate, '')}</p>}
      />
      <Section
        titleId='benefits.deposit.modal.overview.depositStatusFrame.sectionTitles.depositAmount'
        content={<p>{state.depositValue} €</p>}
      />
      {isEntitled && (
        <Section
          titleId='benefits.deposit.modal.overview.depositStatusFrame.sectionTitles.monthlyContributionEntitled'
          content={
            <>
              <p>
                {t({
                  id: 'benefits.deposit.modal.overview.depositStatusFrame.monthlyContributionEntitledInfo',
                  defaultMessage:
                    'Nach Beendigung deiner Mieter-Plus-Mitgliedschaft entstehen für die Mietkautionsbürgschaft der Allianz Kosten in Höhe von 4,7% deiner Kautionssumme pro Jahr. Diese Kosten sind bis zur Rückgabe der Kautionsurkunde im Original über deine hinterlegte Zahlungsmethode an uns anteilig pro Monat zu bezahlen.',
                })}
              </p>
              <div className={'margin-top-m'}>
                <Link to={'/mietkautionsburgschaft/faqs#cancellation'}>
                  <i className={`is24-icon-info`} />
                  {t({
                    id: 'benefits.deposit.modal.overview.depositStatusFrame.faqLinkText',
                    defaultMessage: 'Wie kann ich kündigen?',
                  })}
                </Link>
              </div>
            </>
          }
        />
      )}
      {!isEntitled && (
        <Section
          titleId='benefits.deposit.modal.overview.depositStatusFrame.monthlyContribution'
          content={<p>{state.monthlyFee} €</p>}
        />
      )}
    </div>
  );
};

export default DepositStatusFrame;
